import { snapToSixteenth } from "./feetInches";
// Building blocks for takeoff commands (see commandHistory.js).
//
// An edit is described as a PATCH — only the fields it changes — never as a
// whole-row snapshot. Undo applies the "before" patch on top of the row AS IT IS
// NOW, so reversing a move cannot silently revert an unrelated edit made after it
// (a rename, a template change, a note). Pure; RoomTakeoff supplies the I/O.

export const ROOM_MOVE_KEYS = ["SheetID", "CanvasX", "CanvasY", "CanvasRot", "PolygonJson"];
export const ROOM_RESIZE_KEYS = ["LengthFt", "WidthFt", "CanvasX", "CanvasY", "PolygonJson"];
export const ROOM_RESHAPE_KEYS = ["CanvasX", "CanvasY", "LengthFt", "WidthFt", "PolygonJson"];

/** The named fields of a row, as they are now (missing fields come back null, not undefined). */
export function pickFields(row, keys) {
  const out = {};
  for (const k of keys) out[k] = row?.[k] ?? null;
  return out;
}

/**
 * What a move changes: position, rotation, the drawing it sits on, and — for a traced
 * room — its stored outline, which is in ABSOLUTE world feet and must travel with it.
 *   translatePolygon(polygonJson, dx, dy) -> polygonJson   (supplied by the caller)
 */
export function roomMovePatch(latest, { x, y, rot }, { activeSheetId = null, translatePolygon } = {}) {
  let polygon = latest.PolygonJson ?? null;
  if (polygon && translatePolygon) polygon = translatePolygon(polygon, x - (+latest.CanvasX || 0), y - (+latest.CanvasY || 0));
  return { SheetID: latest.SheetID ?? activeSheetId ?? null, CanvasX: x, CanvasY: y, CanvasRot: rot, PolygonJson: polygon };
}

/**
 * What a canvas resize changes: the dimensions, the outline (a resized room is a plain
 * rectangle again — db/21), and, when a west / north edge moved, its anchor. One patch,
 * so it is one save and one Undo step.
 */
export function roomResizePatch(lengthFt, widthFt, pos) {
  return { LengthFt: snapToSixteenth(lengthFt), WidthFt: snapToSixteenth(widthFt), PolygonJson: null, ...(pos ? { CanvasX: pos.x, CanvasY: pos.y } : {}) };
}

const num = (v) => (v == null || v === "" ? null : +v);

// A drawn-condition row → the sp_SaveTakeoffConditionDrawn payload that re-creates it
// (identity fields, both faces, geometry, mark, deduction role and policy). The id is
// NOT carried — the proc assigns a new one; the command keeps it in its own closure.
export function conditionRowToPayload(row, revisionId) {
  return {
    revision_id: revisionId,
    sheet_id: num(row.SheetID),
    condition_type_id: num(row.TakeoffConditionLibraryID),
    revision_condition_type_id: num(row.TakeoffConditionTypeID),
    kind: row.Kind || null, item_id: num(row.ItemID), assembly_id: num(row.AssemblyID),
    condition_type_id_face_b: num(row.TakeoffConditionLibraryIDFaceB),
    revision_condition_type_id_face_b: num(row.TakeoffConditionTypeIDFaceB),
    kind_face_b: row.KindFaceB || null, item_id_face_b: num(row.ItemIDFaceB), assembly_id_face_b: num(row.AssemblyIDFaceB),
    room_id: num(row.RoomID), room_b_id: num(row.RoomBID),
    mark: row.Mark ?? null,
    target_type: row.TargetType || "drawn",
    geometry_json: row.GeometryJson ?? null,
    height_ft_override: num(row.HeightFtOverride),
    quantity_override: num(row.QuantityOverride),
    notes: row.Notes ?? null,
    display_order: num(row.DisplayOrder) ?? 100,
    measure_role: row.MeasureRole || undefined,
    applies_to_room_element_id: num(row.AppliesToRoomElementID) ?? undefined,
    deduction_policy: row.DeductionPolicy || undefined,
  };
}

/**
 * The payload for changing ONE drawn condition's geometry / room. The proc writes
 * HeightFtOverride, QuantityOverride, Notes and DisplayOrder VERBATIM on an update (an
 * omitted one becomes NULL), so they are always re-sent from the row as it is now — which
 * is also what keeps a symbol move from wiping its height override. Mark, RoomBID, face
 * assignments and the like are preserve-if-omitted and are left out.
 */
export function conditionPatchPayload(latest, patch, revisionId, { lockPosition = false } = {}) {
  return {
    takeoff_condition_id: latest.TakeoffConditionDrawnID,
    revision_id: revisionId,
    room_id: num("RoomID" in patch ? patch.RoomID : latest.RoomID),
    geometry_json: "GeometryJson" in patch ? patch.GeometryJson : latest.GeometryJson,
    height_ft_override: num(latest.HeightFtOverride),
    quantity_override: latest.QuantityOverride ?? 1,
    notes: latest.Notes ?? null,
    display_order: latest.DisplayOrder ?? 100,
    ...(lockPosition ? { auto_position_locked: 1 } : {}),
  };
}

/** Rows with the patch laid over the one with this id (optimistic local update / rollback). */
export function patchRowById(rows, idKey, id, patch) {
  return rows.map(r => (r[idKey] === id ? { ...r, ...patch } : r));
}
